import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import { useState } from 'react';
import { isNullish } from 'remeda';
import { DynamicIcon } from 'src/components';
import {
  BulkUpdateEntitiesProgress,
  BulkUpdateErrorRecord,
  isProgressTerminal,
} from 'src/graphql/entities/custom-view/custom-view-bulk-update';
import {
  BulkUpdateOperationKind,
  getBulkUpdateBannerTitle,
  getBulkUpdateProgressDetails,
} from './bulk-update-banner.helpers';

const MAX_VISIBLE_RECORDS = 50;

const BulkUpdateErrorRecordLink = ({
  entityName,
  record,
  partnerPrefix,
}: {
  entityName?: string;
  record: BulkUpdateErrorRecord;
  partnerPrefix?: string;
}) => {
  const text = record.label || record.id;

  if (entityName !== 'Address') {
    return (
      <Typography variant="caption" color="text.secondary">
        {text}
      </Typography>
    );
  }

  const href = `${partnerPrefix ? `/${partnerPrefix}` : ''}${portalPaths.site.full(record.id)}`;
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      variant="caption"
      sx={{ width: 'fit-content' }}
    >
      {text}
    </Link>
  );
};

interface BulkUpdateJobBannerProps {
  progress?: BulkUpdateEntitiesProgress;
  isLoading: boolean;
  isUndoInProgress: boolean;
  hasSelectionBar?: boolean;
  operationKind?: BulkUpdateOperationKind;
  partnerPrefix?: string;
  onUndo: () => void;
  onClose: () => void;
}

export const BulkUpdateJobBanner = ({
  progress,
  isLoading,
  isUndoInProgress,
  hasSelectionBar = false,
  operationKind = 'update',
  partnerPrefix,
  onUndo,
  onClose,
}: BulkUpdateJobBannerProps) => {
  const [isErrorsDialogOpen, setIsErrorsDialogOpen] = useState(false);
  const status = progress?.status?.toLowerCase();
  const isTerminal = isProgressTerminal(status);

  if (!isLoading && !progress) {
    return null;
  }
  // Real poll-driven progress — deliberately no wall-clock simulation, so a stalled job looks stalled.
  const canUndo = Boolean(
    progress &&
      (status === 'done' || status === 'partial_failure') &&
      !progress.undoUsedAt &&
      (!progress.undoIneligibilityCode || progress.undoIneligibilityCode === 'none'),
  );
  const progressPercent =
    progress && progress.total > 0
      ? Math.max(0, Math.min(100, Math.round((progress.processed / progress.total) * 100)))
      : 0;
  const title = getBulkUpdateBannerTitle({ operationKind, status, isUndoInProgress });
  const details = getBannerDetails({
    status,
    isUndoInProgress,
    progress,
    operationKind,
  });
  const hasErrors = Boolean(
    (progress?.failedCount ?? 0) > 0 || (progress?.errors?.length ?? 0) > 0,
  );

  return (
    <>
      <Stack
        sx={(theme) => ({
          position: 'fixed',
          left: '50%',
          bottom: hasSelectionBar ? '5.5rem' : '1.5rem',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          borderRadius: '6.25rem',
          padding: '0.0625rem',
          background: `linear-gradient(135deg, ${theme.palette.custom.OV.sys.secondary}80, ${theme.palette.custom.OV.sys.outlineVariant}40)`,
          boxShadow: `0 0.5rem 2rem ${theme.palette.custom.OV.sys.scrim}33`,
          width: 'max-content',
          maxWidth: 'calc(100vw - 2rem)',
        })}
        direction="row"
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={(theme) => ({
            minWidth: '17rem',
            maxWidth: 'min(56rem, calc(100vw - 2rem))',
            borderRadius: 'inherit',
            px: '1.25rem',
            py: '0.375rem',
            position: 'relative',
            overflow: 'hidden',
            backdropFilter: 'blur(1.25rem) saturate(180%)',
            WebkitBackdropFilter: 'blur(1.25rem) saturate(180%)',
            backgroundColor: `${theme.palette.custom.OV.sys.surfaceContainerHigh}B3`,
          })}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
              {title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                minWidth: 0,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {details}
            </Typography>
            {!isNullish(progress?.undoIneligibilityCode) &&
            progress?.undoIneligibilityCode !== 'none' ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap' }}
              >
                Undo unavailable:{' '}
                {progress?.undoIneligibilityMessage || progress?.undoIneligibilityCode}
              </Typography>
            ) : null}
          </Stack>

          {hasErrors ? (
            <Button size="small" variant="text" onClick={() => setIsErrorsDialogOpen(true)}>
              View errors
            </Button>
          ) : null}
          {canUndo ? (
            <Button
              size="small"
              variant="outlined"
              onClick={onUndo}
              disabled={isUndoInProgress}
            >
              {isUndoInProgress ? 'Undoing...' : 'Undo'}
            </Button>
          ) : null}
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Dismiss bulk update banner"
            sx={{ flex: '0 0 auto' }}
          >
            <DynamicIcon name="close" fontSize="small" />
          </IconButton>

          {!isTerminal || isLoading ? (
            <LinearProgress
              variant={progress?.total ? 'determinate' : 'indeterminate'}
              value={progress?.total ? progressPercent : undefined}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 2,
              }}
            />
          ) : null}
        </Stack>
      </Stack>

      <Dialog
        open={isErrorsDialogOpen}
        onClose={() => setIsErrorsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Bulk update errors</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {(progress?.errors?.length ?? 0) === 0 ? (
              <Typography variant="body2" color="text.secondary">
                This operation reported failures, but no detailed error payload was returned.
              </Typography>
            ) : (
              progress?.errors?.map((group) => {
                const records = group.records ?? [];
                return (
                  <Stack
                    key={group.message}
                    spacing={0.75}
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                      borderRadius: 1,
                      p: 1,
                    })}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {group.message}
                    </Typography>
                    {records.length > 0 && (
                      <Stack spacing={0.25}>
                        {records.slice(0, MAX_VISIBLE_RECORDS).map((record) => (
                          <BulkUpdateErrorRecordLink
                            key={record.id}
                            entityName={progress?.entityName}
                            record={record}
                            partnerPrefix={partnerPrefix}
                          />
                        ))}
                        {records.length > MAX_VISIBLE_RECORDS && (
                          <Typography variant="caption" color="text.secondary">
                            ...and {records.length - MAX_VISIBLE_RECORDS} more
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Stack>
                );
              })
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

function getBannerDetails(input: {
  status?: string;
  isUndoInProgress: boolean;
  progress?: BulkUpdateEntitiesProgress;
  operationKind: BulkUpdateOperationKind;
}): string {
  if (input.isUndoInProgress) {
    return 'Reverting previous changes...';
  }

  if (input.status?.toLowerCase() === 'undone') {
    const conflictSuffix = input.progress?.undoConflictCount
      ? ` • ${input.progress.undoConflictCount} conflict(s) skipped`
      : '';

    return `${Math.max(0, input.progress?.updatedCount ?? 0)} reverted${conflictSuffix}`;
  }

  return getBulkUpdateProgressDetails({
    operationKind: input.operationKind,
    progress: input.progress,
  });
}
